import type { Position, Size } from '../../types';
import type {
  EditorImmerGetState,
  EditorImmerSetState,
} from './editor-store-types';

export interface PendingContainerSlice {
  // Pending container tracking (for slash command overlap prevention)
  pendingContainerPositions: Map<
    string,
    { position: Position; size: Size; timestamp: number }
  >;

  // Pending container tracking actions
  registerPendingContainer: (
    id: string,
    position: Position,
    size: Size,
  ) => void;
  clearPendingContainer: (id: string) => void;
  getPendingContainerPositions: () => Map<
    string,
    { position: Position; size: Size; timestamp: number }
  >;
  cleanupStalePendingContainers: () => void;
}

export const pendingContainerInitialState: Pick<
  PendingContainerSlice,
  'pendingContainerPositions'
> = {
  // Pending container tracking initial state
  pendingContainerPositions: new Map(),
};

export function createPendingContainerSlice<
  TState extends PendingContainerSlice,
>(
  set: EditorImmerSetState<TState>,
  get: EditorImmerGetState<TState>,
): PendingContainerSlice {
  return {
    ...pendingContainerInitialState,

    // Pending container tracking actions
    registerPendingContainer: (id, position, size) => {
      set((state) => {
        state.pendingContainerPositions.set(id, {
          position,
          size,
          timestamp: Date.now(),
        });
      });
    },

    clearPendingContainer: (id) => {
      set((state) => {
        state.pendingContainerPositions.delete(id);
      });
    },

    getPendingContainerPositions: () => {
      return get().pendingContainerPositions;
    },

    cleanupStalePendingContainers: () => {
      const now = Date.now();
      const STALE_THRESHOLD = 5000; // 5 seconds

      set((state) => {
        const staleIds: string[] = [];
        state.pendingContainerPositions.forEach((data, id) => {
          if (now - data.timestamp > STALE_THRESHOLD) {
            staleIds.push(id);
          }
        });

        staleIds.forEach((id) => {
          state.pendingContainerPositions.delete(id);
        });
      });
    },
  };
}
